import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INSIGHTS_AGENT_NAME } from '../../lib/agentBranding';
import type { AgentFocus } from '../../types';
import { useSpeechAsk } from '../assistant/useSpeechAsk';
import { Icon } from '../shared/Icon';

/** What the next question carries: the focus of "Ask about this" and the
 *  shift filter, both visible before sending (docs/18 §4). */
function ContextChips({
  focus,
  onClearFocus,
  shifts,
}: {
  focus: AgentFocus | null;
  onClearFocus: () => void;
  shifts: string[];
}) {
  const { t } = useTranslation('assistant');
  if (!focus && shifts.length === 0) return null;
  return (
    <div className="maitre-context-chips">
      {focus ? (
        <span className="maitre-chip is-focus" title={focus.detail ?? focus.label}>
          <span className="maitre-chip-text">
            {t('maitre.about', { label: focus.label })}
          </span>
          <button
            type="button"
            className="maitre-chip-x"
            onClick={onClearFocus}
            aria-label={t('maitre.removeFocus', { label: focus.label })}
          >
            <Icon name="close" size={11} />
          </button>
        </span>
      ) : null}
      {shifts.length ? (
        <span className="maitre-chip">{t('maitre.shifts', { names: shifts.join(', ') })}</span>
      ) : null}
    </div>
  );
}

export function MaitreComposer({
  variant,
  busy,
  onSend,
  onStop,
  focus,
  onClearFocus,
  shifts,
  placeholder,
  autoFocus = false,
  spotlight = false,
  inputRef: externalRef,
}: {
  variant: 'panel' | 'home';
  busy: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  focus: AgentFocus | null;
  onClearFocus: () => void;
  shifts: string[];
  placeholder?: string;
  autoFocus?: boolean;
  spotlight?: boolean;
  inputRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
}) {
  const { t } = useTranslation('assistant');
  const [input, setInput] = useState('');
  const localRef = useRef<HTMLTextAreaElement | null>(null);
  const ref = externalRef ?? localRef;

  const send = (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;
    setInput('');
    onSend(text);
  };

  const speech = useSpeechAsk({
    disabled: busy || variant !== 'home',
    onTranscript: text => setInput(text),
    onAutoSend: text => send(text),
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input, ref]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus, ref]);

  const chips = <ContextChips focus={focus} onClearFocus={onClearFocus} shifts={shifts} />;
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  if (variant === 'panel') {
    return (
      <div className="agent-composer-wrap">
        {chips}
        <div className="agent-composer" data-ftue="agent-composer">
          <textarea
            ref={ref}
            className="agent-input"
            placeholder={placeholder ?? t('ask.phAsk')}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
          />
          <button
            type="button"
            className={`agent-send${busy ? ' is-stop' : ''}`}
            onClick={() => (busy ? onStop() : send(input))}
            disabled={!busy && !input.trim()}
            aria-label={busy ? t('view.stop') : t('ask.send')}
            title={busy ? t('view.stop') : t('ask.send')}
          >
            {busy ? <Icon name="stop" size={14} fill /> : <Icon name="arrowUp" size={18} />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="maitre-composer-wrap">
      {chips}
      <div className={`maitre-composer${spotlight ? ' is-spotlight' : ''}`} data-ftue="assistant-ask">
        <button
          type="button"
          className={`maitre-mic${speech.listening ? ' is-on' : ''}${!speech.available ? ' is-off' : ''}`}
          onClick={speech.toggle}
          disabled={!speech.available || busy}
          title={
            speech.available
              ? speech.listening
                ? t('view.stopListening')
                : t('view.talk')
              : t('view.voiceUnavailableTip')
          }
          aria-label={
            speech.available
              ? speech.listening
                ? t('view.stopListening')
                : t('view.startListening')
              : t('view.voiceUnavailable')
          }
        >
          <Icon name="mic" size={18} />
        </button>
        <textarea
          ref={ref}
          className="maitre-input"
          rows={1}
          value={input}
          placeholder={
            speech.listening
              ? t('view.listening')
              : placeholder ?? t('view.askPh', { name: INSIGHTS_AGENT_NAME })
          }
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
        />
        <button
          type="button"
          className={`maitre-send${busy ? ' is-stop' : ''}`}
          onClick={() => (busy ? onStop() : send(input))}
          disabled={!busy && !input.trim()}
          aria-label={busy ? t('view.stop') : t('view.ask')}
          title={busy ? t('view.stop') : t('view.ask')}
        >
          {busy ? <Icon name="stop" size={14} fill /> : t('view.ask')}
        </button>
      </div>
    </div>
  );
}
